import React, { useState, useMemo, useEffect } from 'react';
import { gql } from '@apollo/client';
import { useQuery, useMutation } from 'hooks/apollo';
import { FormattedMessage } from 'react-intl';
import { Select, Button, Tag, Input, Space, Row, Col } from 'antd';
import { PlusOutlined, MinusOutlined, EditOutlined, SearchOutlined, DeleteOutlined, TranslationOutlined, WarningOutlined, LoadingOutlined } from '@ant-design/icons';
import equal from 'fast-deep-equal/react';

import Table from 'components/Table';
import SelectOrAdd from 'components/SelectOrAdd';
import { openGoogleTranslate } from 'components/TranslationButton';
import theme from 'shared/constants/theme.json';
import globalMessages from 'translations/messages';
import useFormatMessage from 'hooks/useFormatMessage';
import useDataSource from 'hooks/useDataSource';
import { AUTO_TAG } from 'shared/constants/permissions';
import { JobStatus, TaggedCommentType } from 'shared/services/prisma/types';
import Brain from 'components/Icons/Brain';
import messages from './messages';
import AutoTagComments from './AutoTagComments';
import RenameTag from './RenameTag';
import AutoSuggestTags from './AutoSuggestTags';

const { Option, OptGroup } = Select;
const { CheckableTag } = Tag;

const GET_TAGS = gql`
  query($questionIds: [ID!]!) {
    themes: commentTags(questionIds: $questionIds) {
      id
      text
      type
      commentCount
    }
    sentiments: commentTags(type: SENTIMENT) {
      id
      text
      type
    }
  }
`;

const GET_QUESTIONS = gql`
  query($ids: [ID!]!) {
    questions(ids: $ids) {
      id
      autoTaggings {
        id
        status
      }
    }
  }
`;

const GET_RESPONSES_WITH_FULL_RESPONSE = gql`
  query($surveyIds: [ID!]!, $responseIds: [ID!]!) {
    questionResponses(surveyIds: $surveyIds, responseIds: $responseIds) {
      id
      surveyResponse {
        id
        questionResponses {
          id
          question {
            id
          }
          options {
            id
          }
        }        
      }
    }
  }
`;

const GET_SURVEY_QUESTIONS = gql`
  query($surveyId: ID!) {
    survey(id: $surveyId) {
      id
      questions {
        id
        title
        group {
          id
          questions {
            id
          }
        }
        options {
          id
          title
          group {
            id
            options {
              id              
            }
          }
        }        
      }
    }
  }
`;

const ADD_THEME = gql`
  mutation($text: String!, $questionIds: [ID!]!) {
    addTheme(text: $text, questionIds: $questionIds) {
      id
    }
  }
`;

const REMOVE_THEME = gql`
  mutation($id: ID!) {
    removeTheme(id: $id) {
      id
    }
  }
`;

const UNTAG_COMMENTS = gql`
  mutation($input: TagCommentsInput!) {
    untagComments(input: $input) {
      id
      tags {
        id
      }
      autoTag {
        id
        tags { id }
        sentiments { id }
      }
    }
  }
`;
const TAG_COMMENTS = gql`
  mutation($input: TagCommentsInput!) {
    tagComments(input: $input) {
      id
      tags {
        id
      }
      autoTag {
        id
        tags { id }
        sentiments { id }
      }
    }
  }
`;

const tableColumns = ({ themes, sentiments, onClickTag, $t, questions }) => {
  const renderTags = ({ tags = [], autoTags = [], allTags = [], rowData, translate }) => allTags.map(({ id, text, type }) => {
    const tagged = tags.includes(id);
    const autoTagged = autoTags.includes(id);
    const checked = tagged || autoTagged;
    return (
      <CheckableTag
        onChange={state => onClickTag(state, { id, text, type }, rowData)}
        key={text}
        checked={checked}
        style={{ borderColor: checked ? '' : theme.borderColor, backgroundColor: checked && theme[text] }}
      >
        {autoTagged ? <Brain style={{ marginRight: 4, fontSize: '0.8em' }} /> : null}
        {translate && messages[text] ? $t(messages[text]) : text}
      </CheckableTag>
    );
  },
  );

  return [
    {
      title: <FormattedMessage {...globalMessages.date} />,
      dataIndex: 'date',
      type: 'date',
      width: 160,
      defaultSortOrder: 'descend',
    },
    {
      title: <FormattedMessage {...globalMessages.text} />,
      dataIndex: 'text',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            id="tag-comments-search-input"
            placeholder="Search"
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm({ closeDropdown: true })}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm({ closeDropdown: true })}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button onClick={() => { clearFilters(); confirm(); }} size="small" style={{ width: 90 }}>
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: filtered => <SearchOutlined style={{ color: filtered ? theme['primary-color'] : undefined }} />,
      onFilter: (value, record) => record.text?.toString().toLowerCase().includes(value.toLowerCase()),
      onFilterDropdownOpenChange: open => open && setTimeout(() => document.querySelector('#tag-comments-search-input')?.focus(), 100),
    },
    {
      title: (
        <>
          <Brain style={{ marginRight: 4 }} />
          Translation
        </>
      ),
      dataIndex: 'translation',
      visible: false,
    },
    {
      title: <FormattedMessage {...messages.theme} />,
      width: 150,
      dataIndex: 'themes',
      render: (tags, rowData) => renderTags({ tags, autoTags: rowData.autoThemes, allTags: themes, rowData }),
      filters: [{ text: <i>Without tag</i>, value: false }, ...themes.map(({ id, text }) => ({
        text,
        value: id,
      }))],
      onFilter: (value, record) => (value ? record.themes?.find(t => t === value) : !record.themes?.length),
      sorter: false,
    },
    {
      title: <FormattedMessage {...messages.sentiment} />,
      width: 100,
      align: 'center',
      dataIndex: 'sentiments',
      render: (tags, rowData) => renderTags({ tags, autoTags: rowData.autoSentiments, allTags: sentiments, rowData, translate: true }),
      filters: [{ text: <i>Without sentiment</i>, value: false }, ...sentiments.map(({ id, text }) => ({
        text,
        value: id,
      }))],
      onFilter: (value, record) => (value ? record.sentiments?.find(t => t === value) : !record.sentiments?.length),
      sorter: false,
    },
    ...questions.map(({ id, title }) => ({
      title,
      dataIndex: id,
      visible: false,
    })),
  ];
};

const TagComments = ({ question, reportComment, responses, responsesLoading, displayedSurvey, refetchResponses, surveyIds, responseIds }) => {
  const [renameTag, setRenameTag] = useState(false);
  const [currentFilters, setCurrentFilter] = useState();
  const [questionColumnsVisible, setQuestionColumnsVisible] = useState(false);
  const [generateTags, setGenerateTags] = useState(false);
  const [autoTag, setAutoTag] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const $t = useFormatMessage();
  const { hasDataSourcePermission } = useDataSource();

  // Table and Edit tags state
  const [selectedTagId, setSelectedTagId] = useState(); // Selected tags in Add/Remove/Select <SelectOrAdd />

  const { questionIds } = question;

  const { refetch: refetchTags, loading: tagsLoading, data: { themes = [], sentiments = [] } = {} } = useQuery(GET_TAGS, {
    variables: { questionIds },
  });

  const { loading: questionsLoading, data: { survey } = {} } = useQuery(GET_SURVEY_QUESTIONS, {
    variables: { surveyId: displayedSurvey.id },
  });

  const { loading: fullResponsesLoading, data: fullQuestionResponses } = useQuery(GET_RESPONSES_WITH_FULL_RESPONSE, {
    variables: { surveyIds, responseIds },
    skip: !questionColumnsVisible,
  });

  const { data: { questions } = {}, startPolling, stopPolling, refetch: refetchQuestions } = useQuery(GET_QUESTIONS, {
    variables: { ids: questionIds },
  });

  const isAutoTagging = questions?.some(q => q.autoTaggings.some(at => at.status === JobStatus.Pending || at.status === JobStatus.Running));

  useEffect(() => {
    if (isAutoTagging) {
      setIsPolling(true);
      startPolling(5000);
    } else if (isPolling) {
      stopPolling();
      refetchResponses();
    }
  }, [isPolling, isAutoTagging]);

  // Backend state mutations
  const [addThemeMutation, { loading: createThemeLoading }] = useMutation(ADD_THEME);
  const [removeThemeMutation, { loading: removeThemeLoading }] = useMutation(REMOVE_THEME);

  const [tagMutation, { loading: tagCommentsLoading }] = useMutation(TAG_COMMENTS);
  const [untagMutation, { loading: untagCommentsLoading }] = useMutation(UNTAG_COMMENTS);

  const selectedTag = themes.find(t => t.id === selectedTagId) || sentiments.find(t => t.id === selectedTagId);

  const canAutoTag = hasDataSourcePermission(AUTO_TAG);

  const onVisibleColumnsChange = (columns) => {
    setQuestionColumnsVisible(columns.some(c => survey?.questions.find(q => q.id === c)));
  };

  // <SelectOrAdd /> logic
  const handleAddTheme = async text => {
    await addThemeMutation({ variables: { text, questionIds } });
    refetchTags();
  };

  const handleRemoveTheme = async () => {
    await removeThemeMutation({ variables: { id: selectedTagId } });
    refetchTags();
    setSelectedTagId(null);
  };

  const handleTagsChange = (id) => {
    setSelectedTagId(id);
  };

  const handleTagsMutation = async ({ action, tag = selectedTag, questionResponseIds } = {}) => {
    const variables = {
      input: {
        tagId: tag.id,
        questionResponseIds,
      },
    };

    if (action === 'remove') {
      await untagMutation({ variables });
    } else {
      await tagMutation({ variables });
    }

    refetchTags();
  };

  const onRenameTheme = () => {
    setRenameTag(false);
    refetchTags();
  };

  const onSuggestTagsDone = () => {
    setGenerateTags(false);
    refetchTags();
  };

  const onAutoTagDone = () => {
    setAutoTag(false);
    refetchQuestions();
  };

  const columns = useMemo(() => tableColumns({
    $t,
    themes,
    sentiments,
    onClickTag: (checked, tag, rowData) => handleTagsMutation({ action: checked ? 'add' : 'remove', tag, questionResponseIds: [rowData.id] }),
    questions: survey?.questions.filter(q => !questionIds.includes(q.id) && q.options.length > 0) || [],
  }), [themes, sentiments, questionIds, survey?.questions]);

  const getQuestionId = (id) => survey?.questions.find(q => q.id === id || q.group?.questions.find(gq => gq.id === id))?.id || id;
  const getOption = (id) => survey?.questions.flatMap(q => q.options).find(o => o.id === id || o.group?.options.find(go => go.id === id));

  const comments = useMemo(() => {
    const mappedComments = responses?.map(c => ({
      id: c.id,
      text: c.text,
      date: c.surveyResponse.respondedAt,
      themes: c.tags.filter(tag => themes.find(t => t.id === tag.id)).map(t => t.id),
      autoThemes: c.autoTag?.tags.map(t => t.id) || [],
      sentiments: c.tags.filter(tag => sentiments.find(t => t.id === tag.id)).map(t => t.id),
      autoSentiments: c.autoTag?.sentiments.map(t => t.id) || [],
      translation: c.autoTag?.translation,
      ...fullQuestionResponses?.questionResponses.find(r => r.id === c.id)?.surveyResponse.questionResponses?.reduce((acc, qr) => ({ ...acc, [getQuestionId(qr.question.id)]: qr.options?.map(o => getOption(o.id)?.title).join(', ') || qr.text }), {}),
    })) || [];
    if (currentFilters) {
      const filters = Object.entries(currentFilters).filter(([, values]) => values);

      if (filters.length) {
        return mappedComments.filter(row => filters.every(([key, values]) => {
          const column = columns.find(col => col.dataIndex === key);
          return column?.onFilter && values.some(v => column.onFilter(v, row));
        }));
      }
    }
    return mappedComments;
  }, [responses, themes, sentiments, currentFilters, survey?.questions, fullQuestionResponses, columns]);

  const actionsMenuItems = record => [{
    key: 'reportComment',
    label: <FormattedMessage {...messages.reportComment} />,
    onClick: () => reportComment(record),
    icon: <WarningOutlined />,
  }, {
    key: 'translate',
    label: <FormattedMessage {...messages.translateComment} />,
    onClick: () => openGoogleTranslate({ text: record.text }),
    icon: <TranslationOutlined />,
  }];

  const commentsWithSelectedTag = (selectedTag && comments.filter(r => r.themes?.includes(selectedTag.id)));
  const commentsWithoutSelectedTag = (selectedTag && comments.filter(r => !r.themes?.includes(selectedTag.id)));
  const removeCount = commentsWithSelectedTag?.length || 0;
  const addCount = commentsWithoutSelectedTag?.length || 0;
  const loading = tagsLoading || tagCommentsLoading || untagCommentsLoading || createThemeLoading || removeThemeLoading || fullResponsesLoading;
  const questionResponseIds = comments.map(c => c.id);

  return (
    <>
      <Row gutter={[16, 16]} justify="space-between">
        <Col>
          <Space>
            <SelectOrAdd
              style={{ width: 300 }}
              loading={loading}
              placeholder={$t(messages.selectTag)}
              allowClear
              onAdd={handleAddTheme}
              onChange={handleTagsChange}
              value={selectedTagId}
              addButtonLabel={$t(messages.addTheme)}
            >
              <OptGroup label={$t(messages.sentiment)}>
                {sentiments.map(tag => <Option key={tag.id} value={tag.id}>{messages[tag.text] ? <FormattedMessage {...messages[tag.text]} /> : tag.text}</Option>)}
              </OptGroup>
              <OptGroup label={$t(messages.theme)}>
                {themes.map(tag => <Option key={tag.id} value={tag.id}>{tag.text}</Option>)}
              </OptGroup>
            </SelectOrAdd>
            <Button type="primary" onClick={() => handleTagsMutation({ action: 'add', questionResponseIds: commentsWithoutSelectedTag.map(c => c.id) })} disabled={!selectedTag || !addCount} loading={tagCommentsLoading} icon={<PlusOutlined />}>
              <FormattedMessage {...messages.addTagToAll} values={{ count: addCount }} />
            </Button>
            <Button type="primary" danger onClick={() => handleTagsMutation({ action: 'remove', questionResponseIds: commentsWithSelectedTag.map(c => c.id) })} disabled={!selectedTag || !removeCount} loading={untagCommentsLoading} icon={<MinusOutlined />}>
              <FormattedMessage {...messages.removeTagFromAll} values={{ count: removeCount }} />
            </Button>
            <Button onClick={() => setRenameTag(true)} disabled={selectedTag?.type !== TaggedCommentType.Theme} icon={<EditOutlined />}>
              <FormattedMessage {...messages.editTag} />
            </Button>
            <Button type="primary" danger onClick={handleRemoveTheme} disabled={!selectedTag || selectedTag.type !== 'THEME' || selectedTag.commentCount > 0} loading={removeThemeLoading} icon={<DeleteOutlined />}>
              {selectedTag?.commentCount > 0 ? <FormattedMessage {...messages.tagUsed} values={{ count: selectedTag.commentCount }} /> : <FormattedMessage {...messages.removeTag} />}
            </Button>

            {loading && <LoadingOutlined />}
          </Space>
        </Col>
        {canAutoTag && (
        <Col>
          <Space>
            <Button icon={<Brain />} type="primary" onClick={() => setGenerateTags(true)}>Suggest tags</Button>
            <Button icon={<Brain />} type="primary" onClick={() => setAutoTag(true)} loading={isAutoTagging}>Auto tag</Button>
          </Space>
        </Col>
        )}
        <Col span={24}>

          <Table
            size="middle"
            bordered
            loading={tagsLoading || questionsLoading || responsesLoading}
            columns={columns}
            dataSource={comments}
            actionsMenuItems={actionsMenuItems}
            onChange={(pagination, filters) => {
              if (!equal(currentFilters, filters)) {
                setCurrentFilter(filters);
              }
            }}
            scroll={{
              y: 'calc(100vh - 300px)',
            }}
            tableLayout="auto"
            className="break-word"
            onVisibleColumnsChange={onVisibleColumnsChange}
          />
        </Col>
      </Row>

      <RenameTag tag={renameTag && selectedTag} onCancel={() => setRenameTag(false)} onDone={onRenameTheme} />
      <AutoSuggestTags open={generateTags} onCancel={() => setGenerateTags(false)} onDone={onSuggestTagsDone} tags={themes} comments={comments} questionResponseIds={questionResponseIds} questionIds={questionIds} />
      <AutoTagComments open={autoTag} onCancel={() => setAutoTag(false)} onDone={onAutoTagDone} tags={themes} sentiments={sentiments} questionResponseIds={questionResponseIds} questionIds={questionIds} />
    </>
  );
};

export default TagComments;
